import React, { useContext, useEffect, useRef, useState } from "react";
import { AttributeContext } from "../AttributeContext";
import { getAssociatedCategoriesByAttributeID } from "api/Attributes";
import { toast } from 'Components/DesignSystem/Toast/useToast'
import Accordion from "Components/DesignSystem/Accordion/Accordion";
import Alert from "Components/DesignSystem/Alert/Alert";
import Tag from "Components/DesignSystem/Tag/Tag";
import EmptyState from "Components/DesignSystem/EmptyState/EmptyState";
import Skeleton from "Components/DesignSystem/Skeleton/Skeleton";

/**
 * Read-only "Categories" accordion in the attribute detail pane. Fetch logic is
 * verbatim from the Bootstrap version (lazy fetch on first expand, keyed by
 * attrId ref); only the rendering moved onto DS Accordion/Alert/Tag/EmptyState.
 * ⚠ DS Accordion UNMOUNTS its body while closed (the legacy collapse only
 * CSS-hid it) — the fetched chips live in this component's state, so nothing is
 * lost across a collapse; the fetch still fires once per attribute.
 */
const CategoriesView = () => {
  const { editingItem } = useContext(AttributeContext);

  const [loading, setLoading] = useState({
    status: "",
    message: "",
  });
  const [attValues, setAttValues] = useState(null);
  const [collapse, setCollapse] = useState(false);
  const attrId = useRef(null)

  useEffect(() => {
    setLoading({
      status: "",
      message: "",
      message2: "",
    });
  }, [editingItem.id]);

  const fetchCategoriesData = () => {
    attrId.current = editingItem.id
    setLoading({status: "Loading", message: "Loading..."})
    getAssociatedCategoriesByAttributeID(editingItem.id)
    .then((res) => {
      setAttValues(res)
      setLoading({
        status: "Success",
        message: ""
      })
    })
    .catch(err => {
      toast.error(err.message)
      setLoading({
        status: "Error",
        message: err.message
      })
    })
  }

  useEffect(() => {
    if(!collapse || attrId.current === editingItem.id) return
    setAttValues(null)
    fetchCategoriesData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapse])

  function handle_collapse() {
    setCollapse(!collapse);
  }

  return (
    <Accordion
      title="Categories"
      open={collapse}
      onOpenChange={() => handle_collapse()}
      data-testid="componets-categoriesview-div-1"
      variant="boxed"
    >
      <div className="tw-pb-4">
        {/* Skeleton chips, not a text strip — the module-wide loading grammar
            (user-requested 2026-09-01). */}
        {loading.status === "Loading" && (
          <div
            className="tw-flex tw-flex-wrap tw-items-center tw-gap-2"
            data-testid="attribute-categories-view-skeleton"
          >
            {[88, 64, 96, 72].map((w, i) => (
              <Skeleton key={i} variant="circle" width={`${w}px`} height="26px" />
            ))}
          </div>
        )}

        {loading.status === "Error" && (
          <Alert tone="danger" live data-testid="attribute-categories-view-status">
            {loading.message}
          </Alert>
        )}

        {loading.status === "Success" && attValues && (
          attValues.length > 0 ? (
            <div className="tw-flex tw-flex-wrap tw-gap-2">
              {attValues.map((item, index) => (
                <Tag key={index} label={item.name} variant="meta" />
              ))}
            </div>
          ) : (
            <div className="tw-flex tw-justify-center tw-py-3">
              <EmptyState
                size="sm"
                title="Category Not Found"
                data-testid="attribute-categories-view-empty"
              />
            </div>
          )
        )}
      </div>
    </Accordion>
  );
};

export default CategoriesView;
